import React, { useState } from 'react';
import { useIndustry } from '../IndustryContext';
import { DocPage, PageHeader } from '../components/DocBlocks';
import './QuickstartPage.css';

const DATA = {
  logistics: {
    company:      'Acme Logistics',
    product:      'Logistics-as-a-Service',
    usageAttr:    'Deliveries Made',
    usageUnit:    'deliveries',
    feeComponent: 'Courier Charges',
    pricing:      '₹40 per delivery',
    customer:     'Flipkart Supply Chain',
    taxId:        'AABCF1234M (GSTIN)',
    city:         'Bangalore, KA',
    contractStart:'Apr 1, 2026',
    usageQty:     50,
    usageQtyFmt:  '50',
    rate:         '₹40',
    subtotal:     '₹2,000',
    subtotalNum:  2000,
    discount:     '₹200',
    discountNum:  200,
    taxAmt:       '₹324',
    total:        '₹2,124',
  },
  saas: {
    company:      'TechCorp',
    product:      'API Platform',
    usageAttr:    'API Calls',
    usageUnit:    'calls',
    feeComponent: 'API Usage Fee',
    pricing:      '₹0.001 per call (₹1 per 1,000)',
    customer:     'DataCorp Inc.',
    taxId:        '12ABCDE1234F1Z5 (GSTIN)',
    city:         'Hyderabad, TS',
    contractStart:'Apr 1, 2026',
    usageQty:     50000,
    usageQtyFmt:  '50,000',
    rate:         '₹0.001',
    subtotal:     '₹50',
    subtotalNum:  50,
    discount:     '₹5',
    discountNum:  5,
    taxAmt:       '₹8.10',
    total:        '₹53.10',
  },
  ecommerce: {
    company:      'ShopMart',
    product:      'Payment Processing',
    usageAttr:    'Transactions',
    usageUnit:    'transactions',
    feeComponent: 'Transaction Fee',
    pricing:      '₹2 per transaction',
    customer:     'RetailCo India Ltd.',
    taxId:        '18AAFCT1234M (GSTIN)',
    city:         'Mumbai, MH',
    contractStart:'Apr 1, 2026',
    usageQty:     1000,
    usageQtyFmt:  '1,000',
    rate:         '₹2',
    subtotal:     '₹2,000',
    subtotalNum:  2000,
    discount:     '₹200',
    discountNum:  200,
    taxAmt:       '₹324',
    total:        '₹2,124',
  },
};

const STEPS = [
  'Product',
  'Meter',
  'Fee Component',
  'Rate Card',
  'Customer',
  'Contract',
  'Usage',
  'Invoice',
];

export default function QuickstartPage({ onNavigate }) {
  const { industryId } = useIndustry();
  const d = DATA[industryId] || DATA.saas;
  const [activeStep, setActiveStep] = useState(null);

  return (
    <DocPage>
      <PageHeader
        eyebrow="Getting Started"
        title="Build your first billing flow"
        description={`Follow these 8 steps to go from zero to a working invoice for ${d.company}. By the end you'll have set up a product, priced it, onboarded a customer, recorded usage, and generated a real invoice.`}
      />

      {/* Step tracker */}
      <div className="qs-tracker">
        {STEPS.map((label, i) => (
          <button
            key={label}
            className={`qs-tracker-step${activeStep === i ? ' qs-tracker-step--active' : ''}`}
            onClick={() => {
              setActiveStep(i);
              document.getElementById(`qs-step-${i + 1}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            <span className="qs-tracker-num">{i + 1}</span>
            <span className="qs-tracker-label">{label}</span>
          </button>
        ))}
      </div>

      {/* ── PART 1: CATALOG ───────────────────────────────────────────────── */}
      <div className="qs-part-label">Part 1 — Set up your catalog <span>(done once per product)</span></div>

      <Step
        id="qs-step-1"
        num={1}
        title="Create a product"
        summary={`A product is what you sell. Everything else — pricing, measurement, invoicing — is scoped to it. ${d.company} offers one product.`}
      >
        <Artifact icon="📦" label="Product">
          <ArtifactRow label="Name">{d.product}</ArtifactRow>
          <ArtifactRow label="Company">{d.company}</ArtifactRow>
          <ArtifactRow label="Status">Active</ArtifactRow>
        </Artifact>
      </Step>

      <StepConnector />

      <Step
        id="qs-step-2"
        num={2}
        title="Define what you measure"
        summary={`A meter is the internal metric that tracks consumption. It's separate from pricing — you can change how you price later without changing what you measure.`}
      >
        <Artifact icon="📏" label="Meter" connects="Product">
          <ArtifactRow label="Name">{d.usageAttr}</ArtifactRow>
          <ArtifactRow label="Unit">{d.usageUnit}</ArtifactRow>
          <ArtifactRow label="Attached to">{d.product}</ArtifactRow>
        </Artifact>
      </Step>

      <StepConnector />

      <Step
        id="qs-step-3"
        num={3}
        title="Create a fee component"
        summary={`A fee component is what the customer sees on their invoice. Think of it as the label on a line item. Without a fee component, there's nothing to charge for.`}
      >
        <Artifact icon="🧾" label="Fee Component" connects="Product">
          <ArtifactRow label="Name">{d.feeComponent}</ArtifactRow>
          <ArtifactRow label="Type">Usage-based</ArtifactRow>
          <ArtifactRow label="Measures">{d.usageAttr}</ArtifactRow>
          <ArtifactRow label="Attached to">{d.product}</ArtifactRow>
        </Artifact>
        <p className="qs-step-note">
          This is the line that will appear on every invoice for {d.company}.
        </p>
      </Step>

      <StepConnector />

      <Step
        id="qs-step-4"
        num={4}
        title="Set the price"
        summary={`A rate card defines the pricing rule for a fee component. You're saying: for this line item, in this currency, charge this amount. ${d.company} uses a simple per-unit rate.`}
      >
        <Artifact icon="💰" label="Rate Card" connects="Fee Component">
          <ArtifactRow label="Fee Component">{d.feeComponent}</ArtifactRow>
          <ArtifactRow label="Currency">INR</ArtifactRow>
          <ArtifactRow label="Pricing model">Per unit</ArtifactRow>
          <ArtifactRow label="Rate">{d.pricing}</ArtifactRow>
        </Artifact>
        <p className="qs-step-note">
          Other models — flat monthly fee, graduated tiers, volume-based — are available. Per unit is the simplest starting point.
        </p>
      </Step>

      {/* ── PART 2: CUSTOMER ──────────────────────────────────────────────── */}
      <div className="qs-part-label">Part 2 — Onboard a customer <span>(repeated for each customer)</span></div>

      <Step
        id="qs-step-5"
        num={5}
        title="Add a customer"
        summary={`A customer is the entity you bill. Their currency links them to the right rate cards; their address determines tax jurisdiction.`}
      >
        <Artifact icon="🏢" label="Customer">
          <ArtifactRow label="Name">{d.customer}</ArtifactRow>
          <ArtifactRow label="Tax ID">{d.taxId}</ArtifactRow>
          <ArtifactRow label="Currency">INR</ArtifactRow>
          <ArtifactRow label="Billing address">{d.city}</ArtifactRow>
        </Artifact>
      </Step>

      <StepConnector />

      <Step
        id="qs-step-6"
        num={6}
        title="Create a contract"
        summary={`A contract ties a customer to a product and sets the commercial terms: billing cycle, payment mode, discounts. This is the central object — without it, no billing happens.`}
      >
        <Artifact icon="📋" label="Contract" connects="Customer + Product">
          <ArtifactRow label="Customer">{d.customer}</ArtifactRow>
          <ArtifactRow label="Product">{d.product}</ArtifactRow>
          <ArtifactRow label="Fee component">{d.feeComponent}</ArtifactRow>
          <ArtifactRow label="Billing cycle">Monthly, postpaid</ArtifactRow>
          <ArtifactRow label="Discount">10%</ArtifactRow>
          <ArtifactRow label="Start date">{d.contractStart}</ArtifactRow>
        </Artifact>
        <p className="qs-step-note">
          Postpaid means you bill after usage is recorded. The same product can have different contract terms per customer — one gets 10%, another gets 15%, both use the same rate card.
        </p>
      </Step>

      {/* ── PART 3: BILLING ───────────────────────────────────────────────── */}
      <div className="qs-part-label">Part 3 — Billing cycle <span>(happens every billing period)</span></div>

      <Step
        id="qs-step-7"
        num={7}
        title="Record usage"
        summary={`During April, ${d.customer} used ${d.company}'s service. Each usage event is tagged with the contract and the meter. The system accumulates these across the billing period.`}
      >
        <Artifact icon="📡" label="Usage Event" connects="Contract">
          <ArtifactRow label="Contract">{d.customer} — Apr 2026</ArtifactRow>
          <ArtifactRow label="Attribute">{d.usageAttr}</ArtifactRow>
          <ArtifactRow label="Quantity">{d.usageQtyFmt} {d.usageUnit}</ArtifactRow>
          <ArtifactRow label="Billing period">Apr 1 – Apr 30, 2026</ArtifactRow>
        </Artifact>
        <p className="qs-step-note">
          You can send events in real-time or batch them. An idempotency key prevents double-counting if an event is sent twice.
        </p>
      </Step>

      <StepConnector />

      {/* ── INVOICE ───────────────────────────────────────────────────────── */}
      <Step
        id="qs-step-8"
        num={8}
        title="Invoice generated"
        summary={`At the end of April, the billing engine runs: it applies the rate card to accumulated usage, applies the contract discount, calculates tax, and generates the invoice.`}
        highlight
      >
        <div className="qs-invoice">
          <div className="qs-invoice-header">
            <div>
              <div className="qs-invoice-from">{d.company}</div>
              <div className="qs-invoice-to">Billed to: {d.customer}</div>
            </div>
            <div className="qs-invoice-period">
              <div className="qs-invoice-label">Billing period</div>
              <div className="qs-invoice-value">Apr 1 – Apr 30, 2026</div>
            </div>
          </div>

          <table className="qs-invoice-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="qs-invoice-item">{d.feeComponent}</td>
                <td>{d.usageQtyFmt} {d.usageUnit}</td>
                <td>{d.rate}</td>
                <td>{d.subtotal}</td>
              </tr>
            </tbody>
          </table>

          <div className="qs-invoice-totals">
            <div className="qs-invoice-row"><span>Subtotal</span><span>{d.subtotal}</span></div>
            <div className="qs-invoice-row qs-invoice-row--discount"><span>Discount (10%)</span><span>−{d.discount}</span></div>
            <div className="qs-invoice-row"><span>GST + SGST (18%)</span><span>{d.taxAmt}</span></div>
            <div className="qs-invoice-row qs-invoice-row--total"><span>Total Due</span><span>{d.total}</span></div>
          </div>
        </div>
      </Step>

      {/* ── SYSTEM OVERVIEW ───────────────────────────────────────────────── */}
      <div className="qs-system">
        <div className="qs-system-title">What you built</div>
        <div className="qs-system-diagram">
          <div className="qs-system-col">
            <div className="qs-system-col-label">Catalog</div>
            <SystemNode icon="📦" label="Product" sub={d.product} />
            <div className="qs-system-arrow">↓</div>
            <SystemNode icon="📏" label="Meter" sub={d.usageAttr} />
            <div className="qs-system-arrow">↓</div>
            <SystemNode icon="🧾" label="Fee Component" sub={d.feeComponent} />
            <div className="qs-system-arrow">↓</div>
            <SystemNode icon="💰" label="Rate Card" sub={d.pricing} />
          </div>

          <div className="qs-system-plus">+</div>

          <div className="qs-system-col">
            <div className="qs-system-col-label">Customer</div>
            <SystemNode icon="🏢" label="Customer" sub={d.customer} />
            <div className="qs-system-arrow">↓</div>
            <SystemNode icon="📋" label="Contract" sub="Monthly · Postpaid · 10% off" />
            <div className="qs-system-arrow">↓</div>
            <SystemNode icon="📡" label="Usage Events" sub={`${d.usageQtyFmt} ${d.usageUnit} in Apr`} />
          </div>

          <div className="qs-system-equals">=</div>

          <div className="qs-system-col qs-system-col--result">
            <div className="qs-system-col-label">Result</div>
            <SystemNode icon="✅" label="Invoice" sub={`${d.total} due`} accent />
          </div>
        </div>
      </div>

      {/* ── NEXT STEPS ────────────────────────────────────────────────────── */}
      <div className="qs-next">
        <div className="qs-next-title">Where to go next</div>
        <div className="qs-next-grid">
          <NextCard icon="💡" title="Core Concepts" desc="Understand every term in one place" page="concepts" onNavigate={onNavigate} />
          <NextCard icon="📐" title="Pricing Models" desc="Tiers, volume, min/max, and fixed fees" page="pricing-models" onNavigate={onNavigate} />
          <NextCard icon="📄" title="Contract Settings" desc="Prepaid, discounts, accumulation" page="contract-fee-components" onNavigate={onNavigate} />
          <NextCard icon="🛠️" title="Build a Contract" desc="Step-by-step guided wizard" page="contract-create" onNavigate={onNavigate} />
        </div>
      </div>
    </DocPage>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Step({ id, num, title, summary, children, highlight }) {
  return (
    <div id={id} className={`qs-step${highlight ? ' qs-step--highlight' : ''}`}>
      <div className="qs-step-header">
        <span className="qs-step-num">{num}</span>
        <div>
          <h2 className="qs-step-title">{title}</h2>
          <p className="qs-step-summary">{summary}</p>
        </div>
      </div>
      <div className="qs-step-content">{children}</div>
    </div>
  );
}

function StepConnector() {
  return <div className="qs-step-connector" aria-hidden="true" />;
}

function Artifact({ icon, label, connects, children }) {
  return (
    <div className="qs-artifact">
      <div className="qs-artifact-header">
        <span className="qs-artifact-icon">{icon}</span>
        <span className="qs-artifact-label">{label}</span>
        {connects && <span className="qs-artifact-connects">attaches to {connects}</span>}
      </div>
      <div className="qs-artifact-rows">{children}</div>
    </div>
  );
}

function ArtifactRow({ label, children }) {
  return (
    <div className="qs-artifact-row">
      <span className="qs-artifact-key">{label}</span>
      <span className="qs-artifact-val">{children}</span>
    </div>
  );
}

function SystemNode({ icon, label, sub, accent }) {
  return (
    <div className={`qs-system-node${accent ? ' qs-system-node--accent' : ''}`}>
      <span className="qs-system-node-icon">{icon}</span>
      <div>
        <div className="qs-system-node-label">{label}</div>
        <div className="qs-system-node-sub">{sub}</div>
      </div>
    </div>
  );
}

function NextCard({ icon, title, desc, page, onNavigate }) {
  return (
    <button className="qs-next-card" onClick={() => onNavigate?.(page)}>
      <span className="qs-next-icon">{icon}</span>
      <span className="qs-next-title-text">{title}</span>
      <span className="qs-next-desc">{desc}</span>
    </button>
  );
}
